import { formatDate } from '@angular/common';
import { electronicFormatIBAN, isValidIBAN } from 'ibantools';
import { AccountInformation, AccountMovement, ContactFile, ContactMessage, Fond, Police, PoliceEntity, Rueckkauf } from '../../_generated';
import {
    DocumentDownloadable,
    PAX_FONDS_AKTIEN_EUROPA_EX_SCHWEIZ,
    PAX_FONDS_AKTIEN_SCHWEIZ,
    PAX_FONDS_PORTFOLIO_100,
    PAX_FONDS_PORTFOLIO_25,
    PAX_FONDS_PORTFOLIO_50,
    PAX_FONDS_PORTFOLIO_75,
    PAX_FONDS_PORTFOLIO_QUALITY_SELECT,
    PAX_FONDS_PORTFOLIO_ZIELRENDITE,
} from '../models/downloadable-files.models';
import { AnstellungEnum } from '../models/partner.models';
import {
    CHILDREN_MINIMUM,
    EU_MINIMUM,
    Fondscode,
    GENERAL_MINIMUM,
    PoliceViewAnpassungDisplayOptions,
    PoliceViewDetail,
    PoliceViewList,
    PoliceViewRueckkauf,
    Tarifcodes,
    TOD_MINIMUM,
    VorsorgeartViewEnum,
} from '../models/policy.models';
import { DateUtils } from './date.utils';
import { FileUtils } from './file.utils';
import { MoneyUtils } from './money.utils';
import { PartnerUtils } from './partner.utils';
import ReasonEnum = ContactMessage.ReasonEnum;
import ZahlungsartEnum = PoliceEntity.ZahlungsartEnum;
import TagEnum = Rueckkauf.TagEnum;
import InkassoartEnum = PoliceEntity.InkassoartEnum;
import { Beneficiary } from '../../shared/components/dialog/dialog.service';

interface PaymentFrequencyOption {
    value: Police.ZahlungsartEnum; // Enum value
    name: string; // Display name
}

/**
 * Utility functions for handling policies in the application.
 */
export const PolicyUtils = {
    /**
     * Checks if given IBAN is valid
     *
     * @param iban - The provided IBAN number which needs to be validated
     * @returns Is provided IBAN valid
     */
    isIbanValid(iban?: string | null): boolean {
        const electronicIban = electronicFormatIBAN(iban ?? '');
        if (!electronicIban) {
            return false;
        }
        return isValidIBAN(electronicIban);
    },

    /**
     * Checks if given praemie is valid.
     * This is a copied method from the old kupo frontend.
     *
     * @param praemie - The given praemie
     * @param police - The given police object
     * @param reason - The given reason (ERHOEHUNG_PV / PRAEMIENREDUKTION)
     * @param max3aValues - The max values for 3a
     * @param erwerbsart - The erwerbsart of the logged in partner
     * @returns if valid => null, if not valid => error string
     */
    isPraemieValid(
        praemie: number,
        police: PoliceViewDetail,
        reason: ReasonEnum,
        max3aValues: {
            max3aAngestellt: number;
            max3aSelbststaendig: number;
        },
        erwerbsart: string,
    ): string | null {
        const max3aValue = PartnerUtils.getMax3aValue(erwerbsart, max3aValues);
        const anstellung = PartnerUtils.getAnstellung(erwerbsart);
        if (reason === ReasonEnum.ERHOEHUNG_PV) {
            if (!police.jahrespraemieErhoehungErlaubt) return 'erhoehungNotPossible';
            if (!police.isRisiko)
                return (praemie || 0) > (police?.jahrespraemie || 0)
                    ? police.vorsorgeart !== VorsorgeartViewEnum.Saeule3a || !max3aValue || (praemie || 0) <= max3aValue || anstellung === AnstellungEnum.OhneAnstellung
                        ? null
                        : `jahrespraemieForm.maxError_${anstellung}`
                    : 'numberInvalid';
            if (police.isRisiko && police.todesfallsumme) return (praemie || 0) > (police?.todesfallsumme || 0) ? null : 'numberInvalid';
            if (police.isRisiko && police.erwerbsunfaehigkeitsrente) return (praemie || 0) > (police?.erwerbsunfaehigkeitsrente || 0) ? null : 'numberInvalid';
        } else if (reason === ReasonEnum.PRAEMIENREDUKTION) {
            if (!police.jahrespraemieReduktionErlaubt) return 'numberInvalid';
            if (this.canChangeVorsorgeart(police)) {
                if (!police.isRisiko) return (praemie || 0) < (police?.jahrespraemie || 0) && (praemie || 0) >= CHILDREN_MINIMUM ? null : 'numberInvalid';
            } else {
                if (!police.isRisiko) return (praemie || 0) < (police?.jahrespraemie || 0) && (praemie || 0) >= GENERAL_MINIMUM ? null : 'numberInvalid';
            }
            if (police.isRisiko && police.todesfallsumme)
                return praemie == null || ((praemie || 0) < (police?.todesfallsumme || 0) && (praemie || 0) >= TOD_MINIMUM)
                    ? null
                    : 'jahrespraemieForm.minErrorTodesfall';
            if (police.isRisiko && police.erwerbsunfaehigkeitsrente)
                return praemie == null || ((praemie || 0) < (police?.erwerbsunfaehigkeitsrente || 0) && (praemie || 0) >= EU_MINIMUM)
                    ? null
                    : 'jahrespraemieForm.minErrorErwerbsunfaehigkeit';
        }
        return null;
    },

    /**
     * Creates a localized product name based on the selected language.
     * Falls back to German if the requested language is not available.
     *
     * @param police - The police object containing translated product names
     * @returns The localized product name string
     */
    getLocalizedProduktName(police: Police): {
        de: string;
        fr: string;
        it: string;
    } {
        return {
            de: police.produktDe,
            fr: police.produktFr,
            it: police.produktIt,
        };
    },

    /**
     * Maps a Police object to a PoliceViewList object with localized content.
     *
     * @param police - The source Police object
     * @returns A PoliceViewList object with translated content
     */
    mapToPoliceViewList(police: Police): PoliceViewList {
        return {
            policennummer: police.policennummer,
            produktName: PolicyUtils.getLocalizedProduktName(police),
            vorsorgeart: police.vorsorgeart,
            active: police.policenstatus === Police.PolicenstatusEnum.J,
            zahlungsart: police.zahlungsart,
            saldo: 0,
            isPraemieFaellig: false,
        };
    },

    /**
     * Maps a praemie to a payload for changing the praemie of a policy.
     * The message format and field names must match SAP's expected schema for praemie change requests.
     */
    mapToChangePraemiePayload(policennummer: string, praemie: number, reason: ReasonEnum): ContactMessage {
        return {
            policennummer,
            message: {
                message: `gewünschte Jahresprämie: ${praemie}`,
            },
            reason: reason,
            files: [],
            praemienanpassungInfo: {
                jahrespraemie: praemie,
            },
        };
    },

    /**
     * Maps a funds switch to a payload for changing the funds of a policy.
     * The message format and field names must match SAP's expected schema for funds switch requests.
     */
    mapToChangeFundsPayload(policennummer: string, message: string, files: ContactFile[]): ContactMessage {
        return {
            policennummer,
            message: { message },
            reason: ContactMessage.ReasonEnum.FONDSSWITCH,
            files: files,
        };
    },

    /**
     * Maps a funds switch to a payload for changing the funds of a policy.
     * The message format and field names must match SAP's expected schema for funds switch requests.
     */
    mapToChangeDisabilityPayload(policennummer: string, message: string, files: ContactFile[]): ContactMessage {
        return {
            policennummer,
            message: { message },
            reason: ContactMessage.ReasonEnum.ERWERBSUNFAEHIGKEIT,
            files: files,
        };
    },

    /**
     * Maps a vollmacht to a payload for changing the vollmacht of a policy.
     * The message format and field names must match SAP's expected schema for vollmacht change requests.
     */
    mapToChangeVollmachtPayload(policennummer: string, message: string, files: ContactFile[]): ContactMessage {
        return {
            policennummer,
            message: { message },
            reason: ContactMessage.ReasonEnum.VOLLMACHT_PV,
            files: files,
        };
    },

    /**
     * Maps a risikozwischenversicherung to a payload for changing the risikozwischenversicherung of a policy.
     * The message format and field names must match SAP's expected schema for risikozwischenversicherung change requests.
     */
    mapToChangeRisikozwischenversicherungPayload(policennummer: string, message: string): ContactMessage {
        return {
            policennummer,
            message: { message },
            reason: ContactMessage.ReasonEnum.RISIKOZWISCHENVERSICHERUNG,
            files: [],
        };
    },

    /**
     * Maps a vorsorgeart to a payload for changing the vorsorgeart of a policy.
     * The message format and field names must match SAP's expected schema for vorsorgeart change requests.
     */
    mapToChangeVorsorgeartPayload(policennummer: string, message: string, files: ContactFile[]): ContactMessage {
        return {
            policennummer,
            message: { message },
            reason: ContactMessage.ReasonEnum.VORSORGEART,
            files: files,
        };
    },

    /**
     * Maps a versicherungsdauer to a payload for changing the versicherungsdauer of a policy.
     * The message format and field names must match SAP's expected schema for versicherungsdauer change requests.
     */
    mapToChangeVersicherungsdauerPayload(policennummer: string, message: string): ContactMessage {
        return {
            policennummer,
            message: { message },
            reason: ContactMessage.ReasonEnum.VERSICHERUNGSDAUER,
            files: [],
        };
    },

    /**
     * Maps a praemienfreistellung to a payload for changing the praemienfreistellung of a policy.
     * The message format and field names must match SAP's expected schema for praemienfreistellung change requests.
     */
    mapToChangePraemienfreistellungPayload(policennummer: string, message: string): ContactMessage {
        return {
            policennummer,
            message: { message },
            reason: ContactMessage.ReasonEnum.PRAEMIENFREISTELLUNG,
            files: [],
        };
    },

    /**
     * Maps a dynamik to a payload for changing the dynamik of a policy.
     * The message format and field names must match SAP's expected schema for dynamik change requests.
     */
    mapToChangeDynamikPayload(policennummer: string, message: string): ContactMessage {
        return {
            policennummer,
            message: { message },
            reason: ContactMessage.ReasonEnum.DYNAMIK_AENDERN_PV,
            files: [],
        };
    },

    /**
     * Maps a vorsorgeart to a payload for changing the vorsorgeart of a policy.
     * The message format and field names must match SAP's expected schema for vorsorgeart change requests.
     */
    mapToChangeVersicherungsnehmerPayload(policennummer: string, message: string, files: ContactFile[]): ContactMessage {
        return {
            policennummer,
            message: { message },
            reason: ContactMessage.ReasonEnum.VN_WECHSEL_PV,
            files: files,
        };
    },
    /**
     * Maps a payment frequency to a payload for changing the payment frequency of a policy.
     * The message format and field names must match SAP's expected schema for payment frequency change requests.
     */
    mapToChangePaymentFrequencyPayload(policennummer: string, paymentFrequency: Police.ZahlungsartEnum): ContactMessage {
        const payload = {
            policennummer,
            message: {
                message: `Zahlungsfrequenz: ${PolicyUtils.mapPoliceZahlungsartToViewZahlungsart(paymentFrequency)} (${paymentFrequency})`,
            },
            reason: ContactMessage.ReasonEnum.ZAHLUNGSFREQUENZ,
            files: [],
            zahlungsfrequenzInfo: {
                selectedZahlungsfrequenz: paymentFrequency,
            },
        };
        return payload;
    },

    /**
     * Maps a payment method to a payload for changing the payment payload of a policy.
     * The message format and field names must match SAP's expected schema for payment payload change requests.
     */
    mapToChangePaymentMethodPayload(policennummer: string, paymentMethod: string, iban: string, fileContents: ContactFile[]): ContactMessage {
        return {
            policennummer,
            reason: ContactMessage.ReasonEnum.ZAHLUNGSMETHODE,
            message: {
                message: iban ? `Zahlungsmethode: ${paymentMethod} - Iban: ${iban}` : `Zahlungsmethode: ${paymentMethod}`,
            },
            files: fileContents,
        };
    },

    /**
     * Maps a list of beneficiaries to a payload for changing the beneficiary of a policy.
     * The message format and field names must match SAP's expected schema for beneficiary change requests.
     */
    mapToChangeBeguenstigungIndividualPayload(policenummer: string, beguenstigungen: Beneficiary[], beguenstigungText: string): ContactMessage {
        const payload = {
            policennummer: policenummer,
            reason: ContactMessage.ReasonEnum.BEGUENSTIGUNG,
            files: [],
            message: { message: '' }, // empty string is also send in the old code
            beguenstigungInfo: {
                isIndividual: true,
                individualText: beguenstigungen
                    .map(
                        (beguenstigung) =>
                            `${beguenstigung.vorname} ${beguenstigung.nachname}, ${DateUtils.formatDate(beguenstigung.geburtsdatum || new Date())}, ${
                                beguenstigung.quote
                            }`,
                    )
                    .join('\n'),
            },
        };

        if (beguenstigungText) {
            payload.beguenstigungInfo.individualText += '\n' + beguenstigungText;
        }

        return payload;
    },

    /**
     * Maps a message and files to a payload for changing the beneficiary of a policy.
     * The message format and field names must match SAP's expected schema for beneficiary change requests.
     */
    async mapToChangeBeguenstigungStandardPayload(policenummer: string, message: string, files: File[]): Promise<ContactMessage> {
        const contactFiles = await Promise.all(files.map(async (file) => await FileUtils.fileToFileContent(file)));

        return {
            policennummer: policenummer,
            reason: ContactMessage.ReasonEnum.BEGUENSTIGUNG,
            files: contactFiles,
            message: { message },
        };
    },

    mapToPoliceViewDetail(police: Police): PoliceViewDetail {
        const praemienKonto = police.kontoInformationen?.find((kontoInformation) => PolicyUtils.isPraemienKonto(kontoInformation));

        return {
            policennummer: police.policennummer,
            vertragsbeginn: police.vertragsbeginn,
            vorsorgeart: police.vorsorgeart.toString() as VorsorgeartViewEnum,
            active: police.policenstatus === Police.PolicenstatusEnum.J,
            produktName: PolicyUtils.getLocalizedProduktName(police),
            formattedErlebensfallsumme: MoneyUtils.formatNullableCurrency(police.erlebensfallsumme, police.waehrung),
            formattedTodesfallsumme: MoneyUtils.formatNullableCurrency(police.todesfallsumme, police.waehrung),
            formattedErwerbsunfaehigkeitsrente: MoneyUtils.formatNullableCurrency(police.erwerbsunfaehigkeitsrente, police.waehrung),
            formattedLeibrente: MoneyUtils.formatNullableCurrency(police.leibrente, police.waehrung),
            formattedTechnischerZins: police.technischerZins ? police.technischerZins + '%' : null,
            beguenstigungTodesfall: this.mapViewBeguenstigung(police.beguenstigungTodesfall),
            beguenstigungTodesfallText: police.beguenstigungTodesfallText,
            fondNames: police.fonds.map((fond) => fond.name),
            fonds: police.fonds,

            inkassoart: police.inkassoart,
            rueckkauf: police.rueckkauf !== null ? this.getFormattedRuckkaufView(police) : null,
            formattedRatenpraemie: MoneyUtils.formatNullableCurrency(police.ratenpraemie, police.waehrung),
            zahlungsart: this.mapPoliceZahlungsartToViewZahlungsart(police.zahlungsart),
            formattedJahrespraemie: MoneyUtils.formatNullableCurrency(police.jahrespraemie, police.waehrung),
            formattedEinmalpraemie: MoneyUtils.formatNullableCurrency(police.einmalpraemie, police.waehrung),
            zahlungsmethode: this.mapViewZahlungsmethode(police.inkassoart, police.inkassoartEsrVersand, police.zahlungsart),
            praemienSaldo: praemienKonto?.saldo || 0,
            formattedRueckkaufswertDatum: police?.rueckkauf?.rueckkaufswertDatum || null,
            isRisiko: police.isRisiko,
            isFondspolice: police.isFondspolice,
            isTerzaFlex: police.isTerzaFlex,
            isPaxExtra: police.isPaxExtra,
            isLeibRente: police.isLeibRente,
            isDiamondLife: police.isDiamondLife,
            haupttarifNummer: police.haupttarifNummer,
            beguenstigungTodUnwiderruflich: police.beguenstigungTodUnwiderruflich,
            vnVtUneinheitlich: police.vnVtUneinheitlich,
            kontoInformationen: police.kontoInformationen,
            zahlungsfrequenzWirkungsdatum: police.zahlungsfrequenzWirkungsdatum,
            todesfallsumme: police.todesfallsumme,
            erwerbsunfaehigkeitsrente: police.erwerbsunfaehigkeitsrente,
            jahrespraemie: police.jahrespraemie,
            jahrespraemieErhoehungErlaubt: police.jahrespraemieErhoehungErlaubt,
            jahrespraemieReduktionErlaubt: this.isJahrespraemieReduktionAllowed(police),
            risikozwischenversicherungBeginn: police.risikozwischenversicherungBeginn,
            risikozwischenversicherungEnde: police.risikozwischenversicherungEnde,
        };
    },

    isKinderversicherung(police: Police) {
        return police.produktDe.includes('Pax Kinderversicherung');
    },

    mapViewBeguenstigung(beguenstigungTodesfall: Police.BeguenstigungTodesfallEnum): 'default' | 'individually' | null {
        switch (beguenstigungTodesfall) {
            case Police.BeguenstigungTodesfallEnum._1:
            case Police.BeguenstigungTodesfallEnum._4:
                return null;
            case Police.BeguenstigungTodesfallEnum._2:
                return 'default';
            case Police.BeguenstigungTodesfallEnum._3:
                return 'individually';
            default:
                return null;
        }
    },

    mapPoliceZahlungsartToViewZahlungsart(zahlungsart: Police.ZahlungsartEnum): string {
        switch (zahlungsart) {
            case Police.ZahlungsartEnum._00:
                return 'praemienfrei';
            case Police.ZahlungsartEnum._01:
                return 'yearly';
            case Police.ZahlungsartEnum._02:
                return 'halfYearly';
            case Police.ZahlungsartEnum._04:
                return 'quarterly';
            case Police.ZahlungsartEnum._12:
                return 'monthly';
            default:
                return '';
        }
    },

    mapViewZahlungsartToPoliceZahlungsartEnum(zahlungsart: string): Police.ZahlungsartEnum {
        switch (zahlungsart) {
            case 'praemienfrei':
                return Police.ZahlungsartEnum._00;
            case 'yearly':
                return Police.ZahlungsartEnum._01;
            case 'halfYearly':
                return Police.ZahlungsartEnum._02;
            case 'quarterly':
                return Police.ZahlungsartEnum._04;
            case 'monthly':
                return Police.ZahlungsartEnum._12;
            default:
                return Police.ZahlungsartEnum._00;
        }
    },

    mapViewZahlungsmethode(inkassoart: Police.InkassoartEnum, inkassoartEsrVersand: boolean, zahlungsart: Police.ZahlungsartEnum): string | null {
        if (zahlungsart === Police.ZahlungsartEnum._00) return null;

        switch (inkassoart) {
            case Police.InkassoartEnum._0:
            case Police.InkassoartEnum._4:
                return null;
            case Police.InkassoartEnum._1:
                return inkassoartEsrVersand ? 'einzahlungsschein' : 'dauerauftrag';
            case Police.InkassoartEnum._2:
                return 'lastschriftverfahren';
            case Police.InkassoartEnum._5:
                return 'praemiendepot';
            case Police.InkassoartEnum._6:
                return 'einzahlungsschein';
            default:
                return '';
        }
    },

    getFormattedRuckkaufView(police: Police): PoliceViewRueckkauf | null {
        if (!police || !police.rueckkauf) {
            return null;
        }

        const rueckkauf = police.rueckkauf;
        const currency = police.waehrung;

        const rueckkaufswert = Number(rueckkauf.rueckkaufswert);
        const praemienGuthaben = Number(rueckkauf.praemienGuthaben);
        const fondsvorauszahlung = Number(rueckkauf.fondsvorauszahlung);
        const darlehen = Number(rueckkauf.darlehen);
        const darlehenszins = Number(rueckkauf.darlehenszins);
        const ausstehendePraemien = Number(rueckkauf.ausstehendePraemien);
        const ueberschuss = Number(rueckkauf.ueberschuss);

        const calc_rueckkaufswert = rueckkaufswert + praemienGuthaben - fondsvorauszahlung - darlehen - darlehenszins - ausstehendePraemien;

        return {
            policennummer: police.policennummer,
            rueckkaufswert: rueckkaufswert,
            formattedRueckkaufswert: MoneyUtils.formatCurrency(calc_rueckkaufswert, currency),
            formattedRueckkaufswertDate: formatDate(rueckkauf.rueckkaufswertDatum, 'dd.MM.yyyy', 'de-CH'),
            formattedUeberschuss: MoneyUtils.formatCurrency(ueberschuss, currency),
            formattedTotal: MoneyUtils.formatCurrency(calc_rueckkaufswert + ueberschuss, currency),
            isTagNull: rueckkauf.tag === null,
            isTagVerzicht: rueckkauf.tag === Rueckkauf.TagEnum.VERZICHT,
            isTagRisikoversicherung: rueckkauf.tag === Rueckkauf.TagEnum.RISIKOVERSICHERUNG_RUECKKAUFSWERT_0,
            isTagLeibrentenpolice: rueckkauf.tag === Rueckkauf.TagEnum.LEIBRENTENPOLICE_RUECKKAUFSWERT_0,
            isTagFondspolice: rueckkauf.tag === Rueckkauf.TagEnum.FONDPOLICE_RUECKKAUFSWERT_0,
            tag: rueckkauf.tag,
        };
    },

    isPraemienKonto(kontoInformation: AccountInformation): boolean {
        return kontoInformation.kontoNummer.includes('PRI');
    },

    /**
     * Determines if technical interest rate should be displayed for a policy
     * @param police - The policy view detail object
     * @returns true if technical interest rate should be shown, false otherwise
     */
    showTechnischerZins(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        const isTarifcodePolicy = Object.values(Tarifcodes).includes(police?.haupttarifNummer as Tarifcodes);
        const isRisikoPolicy = police?.isRisiko;

        return !(isTarifcodePolicy || isRisikoPolicy);
    },

    getPoliceViewAnpassungDisplayOptions(police: PoliceViewDetail | undefined): PoliceViewAnpassungDisplayOptions {
        return {
            canChangeBeguenstigung: this.canChangeBeguenstigung(police),
            canChangeFondswitch: this.canChangeFondswitch(police),
            canRequestPraemienfreistellung: this.canRequestPraemienfreistellung(police),
            canChangeVorsorgeart: this.canChangeVorsorgeart(police),
            canChangeVersicherungsnehmer: this.canChangeVersicherungsnehmer(police),
            canChangeDynamik: this.canChangeDynamik(police),
            canChangeAutomatischeAnpassung: this.canChangeAutomatischeAnpassung(police),
            canChangeJahrespraemie: this.canChangeJahrespraemie(police),
            canChangeZahlungsmethode: this.canChangeZahlungsmethode(police),
            canRequestFondsvorauszahlung: this.canRequestFondsvorauszahlung(police),
            canRequestErwerbsunfaehigkeit: this.canRequestErwerbsunfaehigkeit(/*police*/),
            canRequestWiederinkraftsetzung: this.canRequestWiederinkraftsetzung(/*police*/),
            canRequestRisikozwischenversicherung: this.canRequestRisikozwischenversicherung(police),
            canRequestRueckerstattung3a: this.canRequestRueckerstattung3a(police),
            showZahlungsart: this.showZahlungsart(police),
            showRueckkauf: this.showRueckkauf(police),
            showTechnischerZins: this.showTechnischerZins(police),
        };
    },
    /**
     * Determines if the beguenstigung can be changed for a policy
     * @param police - The policy view detail object
     * @returns true if beguenstigung can be changed, false otherwise
     */
    canChangeBeguenstigung(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        return !(police?.vnVtUneinheitlich ?? true) && !(police?.beguenstigungTodUnwiderruflich ?? true);
    },

    /**
     * Determines if the fondswitch can be changed for a policy
     * @param police - The policy view detail object
     * @returns true if fondswitch can be changed, false otherwise
     */
    canChangeFondswitch(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        return police.fonds.length > 1;
    },

    /**
     * Determines if the praemienfreistellung can be applied for a policy
     * @param police - The policy view detail object
     * @returns true if praemienfreistellung can be applied, false otherwise
     */
    canRequestPraemienfreistellung(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        return !police?.isRisiko && police?.zahlungsart !== Police.ZahlungsartEnum._00 && !police?.rueckkauf?.isTagVerzicht;
    },

    /**
     * Determines if the vorsorgeart can be changed for a po  licy
     * @param police - The policy view detail object
     * @returns true if vorsorgeart can be changed, false otherwise
     */
    canChangeVorsorgeart(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        // wth is this logic? (comes from the old code) is there no better way to determine if the policy is a child policy?
        return police.produktName.de.includes('Pax Kinderversicherung');
    },

    /**
     * Determines if the versicherungsnehmer can be changed for a policy
     * @param police - The policy view detail object
     * @returns true if versicherungsnehmer can be changed, false otherwise
     */
    canChangeVersicherungsnehmer(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        // info: Vorsorgeart F is 3b
        // info: Vorsorgeart G is 3a
        // info: Vorsorgeart V is Vadoise and it's like 3b
        return police.vorsorgeart === VorsorgeartViewEnum.Saeule3b;
    },

    /**
     * Determines if the dynamik can be changed for a policy
     * @param police - The policy view detail object
     * @returns true if dynamik can be changed, false otherwise
     */
    canChangeDynamik(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        return !police?.isRisiko && police.vorsorgeart === VorsorgeartViewEnum.Saeule3b;
    },

    canChangeAutomatischeAnpassung(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        return police.vorsorgeart === VorsorgeartViewEnum.Saeule3a;
    },

    canChangeJahrespraemie(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        return police.jahrespraemieErhoehungErlaubt || police.jahrespraemieReduktionErlaubt;
    },

    canChangeZahlungsmethode(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        return police.inkassoart !== InkassoartEnum._0;
    },

    /**
     * Determines if the fondsvorauszahlung can be requested for a policy
     * @param police - The policy view detail object
     * @returns true if fondsvorauszahlung can be requested, false otherwise
     */
    canRequestFondsvorauszahlung(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        return (police && police.rueckkauf?.formattedRueckkaufswert && police.vorsorgeart === VorsorgeartViewEnum.Saeule3b && police.isFondspolice) || false;
    },

    /**
     * Determines if the erwerbsunfaehigkeit can be requested for a policy
     * @param police - The policy view detail object
     * @returns true if erwerbsunfaehigkeit can be requested, false otherwise
     */
    canRequestErwerbsunfaehigkeit() /*police: PoliceViewDetail | undefined*/ : boolean {
        // TODO: implement
        return true;
    },

    /**
     * Determines if the wiederinkraftsetzung can be requested for a policy
     * @param police - The policy view detail object
     * @returns true if wiederinkraftsetzung can be requested, false otherwise
     */
    canRequestWiederinkraftsetzung() /* police: PoliceViewDetail | undefined */ : boolean {
        // TODO: implement
        return false;
    },

    canRequestRisikozwischenversicherung(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        return !(
            police?.zahlungsart === Police.ZahlungsartEnum._00 ||
            police?.isRisiko ||
            police?.isTerzaFlex ||
            police?.isPaxExtra ||
            police?.isLeibRente ||
            police?.isDiamondLife
        );
    },

    /**
     * Determines if the rueckerstattung can be requested for a policy
     * @param police - The policy view detail object
     * @returns true if rueckerstattung can be requested, false otherwise
     */
    canRequestRueckerstattung3a(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        return police.vorsorgeart === VorsorgeartViewEnum.Saeule3a;
    },

    /**
     * Determines if the rueckkauf should be shown for a policy
     * @param police - The policy view detail object
     * @returns true if rueckkauf should be shown, false otherwise
     */
    showRueckkauf(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        return police.rueckkauf !== null && police.formattedRueckkaufswertDatum !== DateUtils.getStartOfTime();
    },

    /**
     * Determines if the zahlungsart should be shown for a policy
     * @param police - The policy view detail object
     * @returns true if zahlungsart should be shown, false otherwise
     */
    showZahlungsart(police: PoliceViewDetail | undefined): boolean {
        if (!police) return false;

        return police.zahlungsart !== 'praemienfrei';
    },

    getPraemienSaldo(police: PoliceViewDetail | undefined): number {
        const praemienKonto = police?.kontoInformationen?.find((kontoInformation) => PolicyUtils.isPraemienKonto(kontoInformation));

        const kontoBewegungen = praemienKonto?.kontoBewegungen || [];

        const praemienSaldo = kontoBewegungen.reduce((acc, kontoBewegung) => {
            if (AccountMovement.StatusEnum.FORDERUNG_OFFEN_UND_FLLIG == kontoBewegung.status) {
                return acc - kontoBewegung.nochOffen;
            }
            return acc;
        }, 0);

        return praemienSaldo;
    },

    getPaymentFrequency(police: PoliceViewDetail | undefined): PaymentFrequencyOption[] {
        if (!police) return [];

        const options =
            police.inkassoart === Police.InkassoartEnum._5
                ? [Police.ZahlungsartEnum._02, Police.ZahlungsartEnum._01]
                : [Police.ZahlungsartEnum._12, Police.ZahlungsartEnum._04, Police.ZahlungsartEnum._02, Police.ZahlungsartEnum._01];

        return options.map((o) => ({
            value: o,
            name: PolicyUtils.mapPoliceZahlungsartToViewZahlungsart(o),
        }));
    },
    getFondsDownloadableDocuments(fond: Fond): DocumentDownloadable | null {
        if (!fond) return null;

        switch (fond.code) {
            case Fondscode.PAX_FONDS_AKTIEN_SCHWEIZ:
                return PAX_FONDS_AKTIEN_SCHWEIZ;
            case Fondscode.PAX_FONDS_AKTIEN_EUROPA_EX_SCHWEIZ:
                return PAX_FONDS_AKTIEN_EUROPA_EX_SCHWEIZ;
            case Fondscode.PAX_FONDS_PORTFOLIO_25:
                return PAX_FONDS_PORTFOLIO_25;
            case Fondscode.PAX_FONDS_PORTFOLIO_50:
                return PAX_FONDS_PORTFOLIO_50;
            case Fondscode.PAX_FONDS_PORTFOLIO_75:
                return PAX_FONDS_PORTFOLIO_75;
            case Fondscode.PAX_FONDS_PORTFOLIO_100:
                return PAX_FONDS_PORTFOLIO_100;
            case Fondscode.PAX_FONDS_PORTFOLIO_ZIELRENDITE:
                return PAX_FONDS_PORTFOLIO_ZIELRENDITE;
            case Fondscode.PAX_FONDS_PORTFOLIO_QUALITY_SELECT:
                return PAX_FONDS_PORTFOLIO_QUALITY_SELECT;
            default:
                return null;
        }
    },

    isJahrespraemieReduktionAllowed(police: Police): boolean {
        if (!!police && police.zahlungsart === ZahlungsartEnum._00) {
            return false;
        } else if (!!police && police.isRisiko) {
            return police.todesfallsumme > TOD_MINIMUM || police.erwerbsunfaehigkeitsrente > EU_MINIMUM;
        } else if (!!police && police.rueckkauf?.tag !== TagEnum.VERZICHT) {
            return this.isKinderversicherung(police) ? police.jahrespraemie > CHILDREN_MINIMUM : police.jahrespraemie > GENERAL_MINIMUM;
        }
        return false;
    },
};
